<template>
  <div class="answers-container mt-4" :class="showEditButton ? 'answers-container-editable' : ''">
    <div
      v-for="(questionGroup, i) in calculatedQuestionGroups" :key="questionGroup.id"
      class="question-group"
    >
      <div v-if="questionGroup.title || questionGroup.description" class="question-group-header">
        <h3>{{ questionGroup.title }}</h3>
        <p v-if="questionGroup.description && !showEditButton" class="question-group-description">
          {{ questionGroup.description }}
        </p>
      </div>
      <div class="question-group-answers container">
        <div
          v-for="(question, i) in questionGroup.questions" :key="question.id"
          class="question-row"
        >
          <div class="separator-container" />
          <div
            class="question-group-question row"
            :class="calculatedQuestionClasses[question.id]"
          >
            <div class="question-container">
              <div v-if="showEditButton" class="question-edit">
                <button
                  class="edit-button" :title="editLabel"
                  @click="editQuestionAnswers(questionGroup, question)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                    class="bi bi-pencil-fill" viewBox="0 0 16 16"
                  >
                    <path
                      d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z"
                    />
                  </svg>
                </button>
              </div>
              <p
                class="question-text" :title="showDebugInfo ? question.identifier : ''"
                @click="editQuestionAnswers(questionGroup, question)"
              >
                <markdown-text>{{ question.question }}</markdown-text>
              </p>
              <p v-if="question.hint && !showEditButton" class="question-hint">
                <markdown-text>{{ question.hint }}</markdown-text>
              </p>
            </div>
            <div class="question-answer-container">
              <div
                v-for="answer in calculatedQuestionAnswers[question.id]"
                class="question-answer-row mb-2"
              >
                <div class="question-answer">
                  <span class="answer-text" :title="showDebugInfo ? answer.identifier : ''">
                    <markdown-text>{{ answer.answer }}</markdown-text>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapState } from 'pinia';
import { useSurveyStore } from '@/store';
import extend from 'extend';
import slugify from 'slugify';
import {
  CONSIDERATION_LEVEL_GREEN,
  CONSIDERATION_LEVEL_RED,
  CONSIDERATION_LEVEL_YELLOW,
} from '@/enum/ConsiderationLevel';
import NoDragImage from '@/components/common/NoDragImage';
import { STATIC_ANSWERS_DIAGNOSES, STATIC_ANSWERS_TREATMENTS } from '@/enum/StaticAnswers';
import MarkdownText from '@/components/common/MarkdownText';
import AppButton from '@/components/common/AppButton';
import PdfGeneratorMixin from '@/mixins/PdfGeneratorMixin';
import SummaryReport from '@/components/Report/SummaryReport';
import { formatDate } from '@/utils/dateHelper';
import { QUESTION_TYPE_MESSAGE } from '../../enum/QuestionType';

export default {
  name: 'AnswersGiven',
  components: { MarkdownText },
  props: {
    questionAnswers: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
    showDebugInfo: {
      type: Boolean,
      required: false,
      default: false,
    },
    showEditButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['editQuestionAnswers'],
  data() {
    return {
      calculatedQuestionGroups: [],
      calculatedQuestionAnswers: {},
      calculatedQuestionClasses: {},
    };
  },
  computed: {
    ...mapState(useSurveyStore, ['questionGroups', 'treatmentCategories', 'diagnosisCategories']),
    editLabel() {
      return 'Edit';
    },
  },
  watch: {
    questionAnswers: {
      handler() {
        this.recalculateCalculatedData();
      },
      deep: true,
    },
  },
  mounted() {
    this.recalculateCalculatedData();
  },
  methods: {
    editQuestionAnswers(questionGroup, question) {
      if (!this.showEditButton) {
        return;
      }

      this.$emit('editQuestionAnswers', {
        questionGroup,
        question,
      });
    },
    recalculateCalculatedData() {
      if (!this.questionAnswers) {
        return;
      }

      const treatmentsAssoc = {};
      const diagnosesAssoc = {};

      for (const treatmentCategory of this.treatmentCategories) {
        for (const treatment of treatmentCategory.items) {
          treatmentsAssoc[treatment.id] = {
            id: treatment.id,
            identifier: treatment.identifier,
            name: `${treatmentCategory.name} - ${treatment.name}`,
            // name: `${treatment.name}`,
          };
        }
      }

      for (const diagnosisCategory of this.diagnosisCategories) {
        for (const diagnosis of diagnosisCategory.items) {
          diagnosesAssoc[diagnosis.id] = {
            id: diagnosis.id,
            identifier: diagnosis.identifier,
            name: `${diagnosisCategory.name} - ${diagnosis.name}`,
            // name: `${diagnosis.name}`,
          };
        }
      }

      const questionAnswerIds = Object.keys(this.questionAnswers);
      const calculatedQuestionGroups = [];
      const calculatedQuestionAnswers = {};
      const calculatedQuestionClasses = {};

      for (const questionGroup of this.questionGroups) {
        const calculatedQuestions = [];

        for (const question of questionGroup.questions) {
          const questionIdString = question.id.toString();

          if (questionAnswerIds.includes(questionIdString)) {
            const answers = this.questionAnswers[questionIdString];

            const calculatedAnswers = [];

            if (answers && answers.length) {
              const questionIdentifierFull = `${questionGroup.identifier}.${question.identifier}`;

              const calculatedQuestion = extend({}, question, {
                identifier: questionIdentifierFull,
              });

              if (this.showEditButton) {
                if (calculatedQuestion.type === QUESTION_TYPE_MESSAGE) {
                  continue;
                }
              }

              calculatedQuestions.push(calculatedQuestion);

              if (question.answers && question.answers.length) {
                // If question has actual options then it's obviously not a static answer

                if (typeof question.answers === 'string') {
                  // Static answer identifier
                  if (question.answers === STATIC_ANSWERS_TREATMENTS) {
                    for (const answer of answers) {
                      calculatedAnswers.push({
                        answer: treatmentsAssoc[answer].name,
                        identifier: `${questionIdentifierFull}:${treatmentsAssoc[answer].identifier}`,
                      });
                    }
                  } else if (question.answers === STATIC_ANSWERS_DIAGNOSES) {
                    for (const answer of answers) {
                      calculatedAnswers.push({
                        answer: diagnosesAssoc[answer].name,
                        identifier: `${questionIdentifierFull}:${diagnosesAssoc[answer].identifier}`,
                      });
                    }
                  } else {
                    throw new Error(`Unknown static answers ${question.answers}`);
                  }
                } else {
                  const questionAnswersAssoc = {};

                  for (const answer of question.answers) {
                    questionAnswersAssoc[answer.id] = answer;
                  }

                  for (const answer of answers) {
                    calculatedAnswers.push({
                      answer: questionAnswersAssoc[answer].answer,
                      identifier: `${questionIdentifierFull}:${questionAnswersAssoc[answer].identifier}`,
                    });
                  }
                }
              } else {
                let i = 0;

                for (const answer of answers) {
                  calculatedAnswers.push({
                    answer,
                    identifier: `${questionIdentifierFull}:${i}`,
                  });

                  i++;
                }
              }

              calculatedQuestionAnswers[questionIdString] = calculatedAnswers;

              const hasMultipleAnswers = calculatedAnswers.length > 1;

              calculatedQuestionClasses[questionIdString] = [
                `question-answer-count-${hasMultipleAnswers ? 'multiple' : 'one'}`,
                `question-type-${question.type}`,
              ];
            }
          }
        }

        if (calculatedQuestions.length) {
          calculatedQuestionGroups.push(extend({}, questionGroup, {
            questions: calculatedQuestions,
          }));
        }
      }

      this.calculatedQuestionAnswers = calculatedQuestionAnswers;
      this.calculatedQuestionGroups = calculatedQuestionGroups;
      this.calculatedQuestionClasses = calculatedQuestionClasses;
    },
  },
};
</script>

<style lang="scss">
@media print {
  .answers-container {
    .question-answer {
      padding: 5px 15px;
      border-radius: 5px;
      //background: #cbe5f1;
      //color: #063C5A;
      font-weight: 0;
    }

    .question-group-header {
      break-inside: avoid;
      //break-after: avoid;

      // Hack to force page changes if the headline is on page 1 and the answers on page 2
      &::after {
        content: "";
        display: block;
        height: 150px;
        margin-bottom: -150px;
      }
    }

    .question-group-answers {
      break-before: avoid;
    }

    .question-group-question {
      break-inside: avoid;
    }
  }
}
</style>
<style lang="scss" scoped>
.answers-container-editable {
  padding-left: 45px;
}

.edit-button {
  position: absolute;
  left: -30px;
  top: 4px;
  border-radius: 10px;
  width: 30px;
  height: 30px;
  background: #45A4CD;
  border: 1px solid black;

  &:hover {
    background: darken(#45a4cd, 10%);
  }

  &:active {
    background: darken(#45a4cd, 15%);
  }

  svg {
    color: white;
    width: 17px;
    height: 17px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

h4 {
  font-weight: 700;
  color: #063C5A;
  font-size: 13pt;
  margin-top: 15px;
  margin-bottom: 15px;
}

.question-group-question {

  //margin-top: 30px;
  padding: 10px 0;
}

.question-group-answers {
  padding: 0;
}

.question-answer-container {
  text-align: right;
}

.question-answer {
  background: #45A4CD;
  padding: 20px;
  border-radius: 10px;
  color: white;
  font-weight: 600;
  text-align: center;
  width: auto;
  display: inline-block;
}

.question-type-division {
  .question-answer-row {
    width: 100%;
    display: block;
  }

  .question-answer {
    width: auto;
  }
}

// Answers on new line
.question-container, .question-answer-container {
  //width: 100%;
  position: relative;
}

.question-answer-row {
  width: auto;
  display: inline-block;
  margin-right: 10px;
}

.question-answer-container, .question-answer {
  text-align: left;
}

</style>
